/**
 * MUI theme factory — creates light or dark theme.
 *
 * The bulk of color switching is done via CSS custom properties (themeTokens.ts).
 * This file configures MUI's palette.mode, background defaults, and CssBaseline.
 */

import { createTheme } from '@mui/material/styles'

import type { ResolvedMode } from '../contexts/ThemeContext'

const fontFamily = [
  'Inter',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  'Arial',
  'sans-serif',
].join(',')

const surfacePaper = {
  backgroundColor: 'var(--bg-surface)',
  color: 'var(--text-primary)',
}

const elevatedBorder = {
  border: '1px solid var(--border-subtle)',
  boxShadow: '0 16px 48px var(--shadow-color)',
}

export function createAppTheme(mode: ResolvedMode) {
  return createTheme({
    palette: {
      mode,
      primary: { main: mode === 'light' ? '#1976d2' : '#7cb8e4' },
      secondary: { main: '#dc004e' },
      background: {
        default: mode === 'light' ? '#f8f9fa' : '#1b1c1d',
        paper: mode === 'light' ? '#fff' : '#1e1f22',
      },
      text: {
        primary: mode === 'light' ? '#202124' : '#f5f7fb',
        secondary: mode === 'light' ? '#5f6368' : '#a6adbb',
      },
      divider: mode === 'light' ? '#e8eaed' : '#2f3034',
    },
    typography: { fontFamily },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            ...surfacePaper,
            ...elevatedBorder,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            ...surfacePaper,
            borderColor: 'var(--border-subtle)',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--menu-bg)',
            color: 'var(--text-primary)',
            border: '1px solid var(--menu-border)',
            boxShadow: '0 12px 32px var(--menu-shadow)',
          },
        },
      },
    },
  })
}
